import { useState, useEffect } from "react";

// Computes invoicePaymentAmount + transactionIds from a set of selected IDs
// and a statement object (with invoiceGroups, creditNotes, creditOnAccount).
export const useInvoicePaymentAmount = (selectedInvoiceIds, statement) => {
  const [invoicePaymentAmount, setInvoicePaymentAmount] = useState(null);
  const [transactionIds, setTransactionIds] = useState(null);

  // Update transactionIds and amount whenever selectedInvoiceIds changes
  useEffect(() => {
    if (!selectedInvoiceIds || !statement?.invoiceGroups) {
      return;
    }

    const selectedArray = Array.from(selectedInvoiceIds);
    setTransactionIds(selectedArray);

    // Calculate the total payment amount for selected IDs
    const roundToTwo = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

    const invoiceTotal = statement.invoiceGroups
      .flatMap((g) => g.invoices)
      .filter((inv) => selectedInvoiceIds.has(inv.ledgerTransactionId))
      .reduce((sum, inv) => sum + inv.remainingAmount, 0);

    const selectedInvoices = statement.invoiceGroups
      .flatMap((g) => g.invoices)
      .filter((inv) => selectedInvoiceIds.has(inv.ledgerTransactionId));

    const nestedCreditNotes = selectedInvoices
      .flatMap((inv) => inv.creditNotes || [])
      .reduce((sum, cn) => sum + cn.remainingAmount, 0);

    const topLevelCreditNotes = statement.invoiceGroups
      .flatMap((g) => g.creditNotes)
      .filter((cn) => selectedInvoiceIds.has(cn.ledgerTransactionId))
      .reduce((sum, cn) => sum + cn.remainingAmount, 0);

    const creditNoteTotal = nestedCreditNotes + topLevelCreditNotes;

    const totalAmount = roundToTwo(
      invoiceTotal + creditNoteTotal + (statement.creditOnAccount || 0)
    );

    setInvoicePaymentAmount(totalAmount);
  }, [selectedInvoiceIds, statement]);

  return [
    invoicePaymentAmount,
    transactionIds,
    setInvoicePaymentAmount,
    setTransactionIds,
  ];
};
